import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, ShoppingBag, TrendingUp, Percent, Plus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export default function WriterDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [sub, setSub] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: my }, { data: subData }] = await Promise.all([
        api.get("/writer/stats"),
        api.get("/my/scripts"),
        api.get("/my/subscription"),
      ]);
      setStats(s); setScripts(my); setSub(subData);
    } catch (e) {
      if (e.response?.status === 402) toast.error("Active writer subscription required. Subscribe to continue.");
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cw-label">Writer Dashboard</div>
          <h1 className="font-display text-4xl mt-2">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          {sub?.plan && <div className="text-xs text-white/50 mt-1">Active plan: <span className="text-[#FF6B00] uppercase">{sub.plan}</span></div>}
        </div>
        <UploadScriptDialog onCreated={load} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={<Wallet />} label="Total Earnings" value={`₹${stats?.total_earnings ?? 0}`} />
        <Stat icon={<ShoppingBag />} label="Scripts Sold" value={stats?.sales_count ?? 0} />
        <Stat icon={<TrendingUp />} label="Monthly Revenue" value={`₹${stats?.monthly_revenue ?? 0}`} />
        <Stat icon={<Percent />} label="Conversion Rate" value={`${stats?.conversion_rate ?? 0}%`} />
      </div>

      <section className="mt-12">
        <h2 className="font-display text-2xl mb-4">My scripts</h2>
        <div className="cw-card rounded-2xl overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/60">
              <tr>
                <th className="p-4">Title</th>
                <th className="p-4">Platform</th>
                <th className="p-4">Price</th>
                <th className="p-4">Sales</th>
                <th className="p-4">Rating</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map((s) => (
                <tr key={s.script_id} className="border-t border-white/5">
                  <td className="p-4 font-display">{s.title}</td>
                  <td className="p-4 text-white/70">{s.platform}</td>
                  <td className="p-4">₹{s.price}</td>
                  <td className="p-4">{s.sales || 0}</td>
                  <td className="p-4">{s.rating || "—"}</td>
                </tr>
              ))}
              {scripts.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-white/50">No scripts yet — upload your first one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl mb-4">Recent sales</h2>
        <div className="cw-card rounded-2xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-widest text-white/60">
              <tr>
                <th className="p-4">Order</th>
                <th className="p-4">Script</th>
                <th className="p-4">Amount</th>
                <th className="p-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent_orders || []).map((o) => (
                <tr key={o.order_id} className="border-t border-white/5">
                  <td className="p-4 font-mono text-xs text-white/60">{o.order_id}</td>
                  <td className="p-4">{o.script_title}</td>
                  <td className="p-4">₹{o.amount}</td>
                  <td className="p-4 text-white/60">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                <tr><td colSpan={4} className="p-8 text-center text-white/50">No sales yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="cw-card rounded-2xl p-6">
      <div className="h-9 w-9 rounded-lg bg-[#FF6B00]/15 border border-[#FF6B00]/30 flex items-center justify-center text-[#FF6B00]">{icon}</div>
      <div className="cw-label mt-4">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

function UploadScriptDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", industry: "Beauty", category: "instagram-reel", platform: "Instagram Reel",
    duration: "15s", hook: "", description: "", content: "",
    price: 499, thumbnail: "https://images.unsplash.com/photo-1664277497095-424e085175e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxjb250ZW50JTIwY3JlYXRvciUyMHZpZGVvfGVufDB8fHx8MTc5MDQ1MTI0N3ww&ixlib=rb-4.1.0&q=85", tags: [],
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/scripts", { ...form, price: parseFloat(form.price) });
      toast.success("Script published");
      setOpen(false);
      onCreated?.();
    } catch (e) {
      if (e.response?.status === 402) toast.error("Active subscription required");
      else toast.error("Failed to publish");
    } finally { setLoading(false); }
  };

  const cls = "bg-black/40 border-white/10 focus:border-[#FF6B00] text-white placeholder:text-white/30";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="cw-btn-primary inline-flex items-center gap-2" data-testid="upload-script"><Plus className="h-4 w-4" /> Upload script</button>
      </DialogTrigger>
      <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload a new script</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={cls} data-testid="upload-title" />
          <div className="grid grid-cols-2 gap-3">
            <Input required placeholder="Industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className={cls} />
            <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
              <SelectTrigger className={cls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="Instagram Reel">Instagram Reel</SelectItem>
                <SelectItem value="YouTube Ad">YouTube Ad</SelectItem>
                <SelectItem value="UGC">UGC</SelectItem>
                <SelectItem value="TikTok">TikTok</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className={cls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="instagram-reel">Instagram Reel</SelectItem>
                <SelectItem value="youtube-ad">YouTube Ad</SelectItem>
                <SelectItem value="ugc">UGC</SelectItem>
                <SelectItem value="viral-hook">Viral Hook</SelectItem>
                <SelectItem value="brand-story">Brand Story</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
              <SelectTrigger className={cls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="15s">15s</SelectItem>
                <SelectItem value="30s">30s</SelectItem>
                <SelectItem value="45s">45s</SelectItem>
                <SelectItem value="60s">60s</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input required placeholder="Hook (first 3 seconds)" value={form.hook} onChange={(e) => setForm({ ...form, hook: e.target.value })} className={cls} />
          <Textarea required placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={cls} />
          <Textarea required placeholder="Full script content" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={cls} />
          <div className="grid grid-cols-2 gap-3">
            <Input required type="number" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={cls} />
            <Input placeholder="Thumbnail URL" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className={cls} />
          </div>
          <button type="submit" disabled={loading} className="cw-btn-primary w-full" data-testid="upload-submit">
            {loading ? "Publishing…" : "Publish script"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
